import { EventDispatcher, Vector2, Vector3 } from 'three';

import type SphereCameraControls from 'shared/renderingEngine/CameraControls';
import { MouseButtons } from 'shared/interfaces/Enums';
import { isLeftMouseButton, isMouseClick, isScrollingTrackPad } from 'shared/utils/InputUtils';
import camera3DEventManager, { Camera3DEvents } from 'shared/eventManager/Camera3DEventManager';

const WHEEL_ZOOM_FACTOR = 0.881;
const TRACKPAD_ZOOM_FACTOR = 0.93;

export interface ClickEvent {
  type: 'click';
  point: Vector2;
}

interface BlurEvent {
  type: 'blur';
}

export interface MoveEvent {
  type: 'move';
  point: Vector2;
}

export type MouseControlsEvent = ClickEvent | BlurEvent | MoveEvent;

class CameraMouseControls extends EventDispatcher<MouseControlsEvent> {
  private cameraControls: SphereCameraControls;

  private pointerIntersection?: Vector3;

  private isDraggingCamera = false;

  private canvasWrapper?: HTMLElement;

  private mouseDownEvent?: MouseEvent;

  init(cameraControls: SphereCameraControls, canvasWrapper: HTMLElement): void {
    this.cameraControls = cameraControls;

    this.canvasWrapper = canvasWrapper;
    this.setupEventListeners(canvasWrapper);
  }

  private setupEventListeners(canvasWrapper: HTMLElement) {
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);

    canvasWrapper?.addEventListener('mousedown', this.onMouseDown, { capture: false });
    canvasWrapper?.addEventListener('mousemove', this.onMouseMove, { capture: false });
    canvasWrapper?.addEventListener('mouseup', this.onMouseUp, { capture: false });
    canvasWrapper?.addEventListener('wheel', this.onWheel, { passive: false });
    canvasWrapper?.addEventListener('mouseout', this.onMouseOut, { capture: false });
  }

  private onMouseDown(event: MouseEvent) {
    this.cameraControls.clearAnimations();
    if (isLeftMouseButton(event)) {
      this.mouseDownEvent = event;
      this.isDraggingCamera = true;
      this.pointerIntersection = this.getSphereIntersection(event);
      camera3DEventManager.trigger(Camera3DEvents.DRAG_CAMERA_START);
    }
  }

  private onMouseMove(event: MouseEvent) {
    this.dispatchEvent({ type: 'move', point: new Vector2(event.clientX, event.clientY) });
    if (!this.isDraggingCamera || !this.pointerIntersection) return;

    const newIntersection = this.getSphereIntersection(event);

    if (!newIntersection) return;

    this.dispatchEvent({ type: 'blur' });

    this.cameraControls.move(newIntersection, this.pointerIntersection);
  }

  private onMouseUp(event: MouseEvent) {
    if (isLeftMouseButton(event)) {
      if (this.mouseDownEvent !== undefined && isMouseClick(this.mouseDownEvent, event))
        this.onMouseClick(event);
      if (this.isDraggingCamera) {
        this.isDraggingCamera = false;
        this.cameraControls.endCameraDrag();
      }
    }
  }

  private onMouseClick(event: MouseEvent) {
    this.dispatchEvent({
      type: 'click',
      point: new Vector2(event.clientX, event.clientY),
    });
  }

  private onMouseOut(event: MouseEvent) {
    this.onMouseUp(
      new MouseEvent('mouseup', {
        buttons: MouseButtons.LEFT,
        clientX: event.clientX,
        clientY: event.clientY,
      }),
    );
  }

  private onWheel(event: WheelEvent) {
    this.cameraControls.clearAnimations();

    let zoomFactor;

    const hardwareFactor = isScrollingTrackPad(event) ? TRACKPAD_ZOOM_FACTOR : WHEEL_ZOOM_FACTOR;

    if (event.deltaY < 0) {
      zoomFactor = hardwareFactor;
    } else if (event.deltaY > 0) {
      zoomFactor = 1 / hardwareFactor;
    }

    if (zoomFactor === undefined) return;

    this.dispatchEvent({ type: 'blur' });
    this.cameraControls.zoomIntoPoint(new Vector2(event.offsetX, event.offsetY), zoomFactor);
  }

  private getSphereIntersection(pointerEvent: MouseEvent) {
    return this.cameraControls.sphereCamera.getMouseIntersection(
      pointerEvent.offsetX,
      pointerEvent.offsetY,
    );
  }

  dispose(): void {
    const canvasWrapper = this.canvasWrapper;
    canvasWrapper?.removeEventListener('wheel', this.onWheel);
    canvasWrapper?.removeEventListener('mousedown', this.onMouseDown);
    canvasWrapper?.removeEventListener('mousemove', this.onMouseMove);
    canvasWrapper?.removeEventListener('mouseup', this.onMouseUp);
    canvasWrapper?.removeEventListener('mouseout', this.onMouseOut);
  }
}

export default CameraMouseControls;
